import { Tab, Tabs } from '@mui/material';
import { useAtom, useAtomValue } from 'jotai';
import { useMemo } from 'react';
import { NavLink, Outlet, useLocation, useNavigate } from 'react-router';

import { PERMISSIONS_LOGIC } from '@/constants/common';
import { AUTO_SOC_TIERS } from '@/constants/products.constants';
import Header from '@/layout/Header/Header';
import MainContent from '@/layout/MainContent';
import { checkPermissions } from '@/services/permissions';
import userAtom from '@/store/user.store';

import { selectedOrganizationAtom } from '../Organizations';
import { ADMINISTRATION_TABS } from './Administration.constants';
import { administrationActionsAtom } from './Administration.store';
import useStyles from './Administration.styles';

const AdministrationTabs = () => {
  const { classes } = useStyles();
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const [user] = useAtom(userAtom);
  const selectedOrganization = useAtomValue(selectedOrganizationAtom);
  const isSecureAutoSoc =
    selectedOrganization.is_auto_soc === AUTO_SOC_TIERS.SECURE;

  const adminTabs = useMemo(
    () =>
      ADMINISTRATION_TABS.filter((tabItem) =>
        checkPermissions(
          tabItem.permissions,
          user,
          selectedOrganization,
          PERMISSIONS_LOGIC.OR
        )
      ).filter((tabItem) => {
        if (isSecureAutoSoc) {
          return tabItem.label !== 'Devices' && tabItem.label !== 'Downloads'; // Hide Devices and Downloads tabs for Secure Auto SOC clients
        }
        return true;
      }),
    [user, selectedOrganization, isSecureAutoSoc]
  );

  const adminTabValue = useMemo(() => {
    const currentTab = adminTabs.find((tabItem) => tabItem.path === pathname);
    return currentTab?.value;
  }, [adminTabs, pathname]);

  // If the tab is not accessible or does not exist, redirect to the main administration page which will then redirect to the first accessible tab
  if (adminTabValue === undefined) {
    navigate('/administration');
    return null;
  }

  return (
    <Tabs
      allowScrollButtonsMobile
      aria-label="admin tabs"
      scrollButtons="auto"
      sx={{ pl: '1.5rem' }}
      value={adminTabValue}
      variant="scrollable"
    >
      {adminTabs.map((tabItem) => (
        <Tab
          key={tabItem.value}
          component={NavLink}
          to={tabItem.path}
          label={tabItem.label}
          value={tabItem.value}
          className={classes.tab}
        />
      ))}
    </Tabs>
  );
};

const Administration = () => {
  const [actionsAtom] = useAtom(administrationActionsAtom);

  return (
    <>
      <Header title="Administration" actionsNewLine actions={actionsAtom}>
        <AdministrationTabs />
      </Header>
      <MainContent>
        <Outlet />
      </MainContent>
    </>
  );
};

export default Administration;
